import { connect, disconnect, getConnections } from "@wagmi/core"
import { useCallback, useRef, useState } from "react"

import { config } from "@/configs"
import { injected } from "wagmi/connectors"
import { useAppKit } from "@reown/appkit/react"
import { useConnection } from "wagmi"
import { useIsMounted } from "@/hooks/use-is-mounted"
import { useQueryClient } from "@tanstack/react-query"

interface UseWalletConnectionProps {
  setIsVerified?: (verified: boolean) => void
  setIsManualConnect?: (manual: boolean) => void
}

export const useWalletConnection = ({
  setIsVerified,
  setIsManualConnect: setIsManualConnectProp,
}: UseWalletConnectionProps = {}) => {
  const { isConnected } = useConnection()
  const { close } = useAppKit()
  const queryClient = useQueryClient()

  const [isManualConnect, setIsManualConnectState] = useState<boolean>(false)
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(false)

  const isClosingDialogRef = useRef(false)
  const isConnectingManuallyRef = useRef(false)

  const isMounted = useIsMounted()

  // Wrapper to update both local state and parent state
  const setIsManualConnect = useCallback(
    (manual: boolean) => {
      setIsManualConnectState(manual)
      setIsManualConnectProp?.(manual)
    },
    [setIsManualConnectProp]
  )

  const disconnectWallet = useCallback(
    async (updateAccessToken?: (token?: string) => void) => {
      try {
        setIsAuthLoading(true)
        isClosingDialogRef.current = true

        await disconnect(config)
        // To ensure all connections are closed
        const connections = getConnections(config)
        connections.forEach(async (connection) => {
          await disconnect(config, { connector: connection.connector })
        })

        // Always clear all cached queries on disconnect to avoid showing stale data
        queryClient.clear()

        if (updateAccessToken) {
          updateAccessToken(undefined)
        }

        // Reset state on disconnect for fresh start
        setIsVerified?.(false)
      } catch {
        // Silent fail
      } finally {
        setTimeout(() => {
          setIsAuthLoading(false)
          isClosingDialogRef.current = false
        }, 200)
      }
    },
    [queryClient, setIsVerified]
  )

  const connectWallet = useCallback(
    async (
      callback?: () => void,
      updateAccessToken?: (token?: string) => void
    ) => {
      const isAvacus = (window as any)?.ethereum?.isAvacus

      // For Avacus: shorter debounce, allow multiple attempts
      // For others: prevent multiple simultaneous attempts
      if (isConnectingManuallyRef.current && !isAvacus) {
        return
      }

      try {
        isConnectingManuallyRef.current = true
        isClosingDialogRef.current = false
        setIsManualConnect(true)

        // Clear any existing token when manually reconnecting
        // This ensures we start fresh with the new address
        if (updateAccessToken) {
          updateAccessToken(undefined)
        }
        setIsVerified?.(false)

        if (isAvacus) {
          await connect(config, { connector: injected() })
          // Close reown modal after connecting with Avacus
          setTimeout(() => close(), 1000)
        } else if (callback) {
          callback()
        }
      } catch {
        setIsManualConnect(false)
        await disconnectWallet(updateAccessToken)
      } finally {
        const timeoutTime = isAvacus ? 100 : 500
        setTimeout(() => {
          isConnectingManuallyRef.current = false
        }, timeoutTime)
      }
    },
    [disconnectWallet, close, setIsVerified, setIsManualConnect]
  )

  return {
    connectWallet,
    disconnectWallet,
    isManualConnect,
    setIsManualConnect,
    isAuthLoading,
    setIsAuthLoading,
    isConnected,
    isClosingDialogRef,
    isConnectingManuallyRef,
    isMounted,
  }
}
